import React, { useEffect } from "react";
import {
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Box,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  useTheme,
  Card,
  Chip,
  Divider,
  Alert,
  Fade,
} from "@mui/material";
import { useForm, FormProvider, Controller } from "react-hook-form";
import { useSelector } from "react-redux";
import {
  usePostVendorMutation,
  usePutVendorMutation,
} from "../../../redux/api/vendor";
import { selectCurrentagency } from "../../../redux/features/authSlice";
import {
  Business,
  ContactPhone,
  LocationOn,
  Assignment,
  Save,
  Update,
  CheckCircle,
} from "@mui/icons-material";

const VendorFormPage = ({ mode = "add", editRow = null, onSuccess }) => {
  const theme = useTheme();
  const currentAgency = useSelector(selectCurrentagency);
  const [showSuccess, setShowSuccess] = React.useState(false);

  const methods = useForm({
    defaultValues: {
      name: "",
      contactPerson: "",
      email: "",
      phone: "",
      city: "",
      address: "",
      licenseNo: "",
      commissionRate: "",
      status: "Active",
    },
    mode: "onChange",
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isValid },
    watch,
  } = methods;

  const [postVendor, { isLoading: isPosting, isSuccess: isPostSuccess }] =
    usePostVendorMutation();
  const [putVendor, { isLoading: isUpdating, isSuccess: isPutSuccess }] =
    usePutVendorMutation();

  const watchedName = watch("name");
  const watchedStatus = watch("status");

  // Populate form if editing
  useEffect(() => {
    if (mode === "edit" && editRow) {
      reset(editRow);
    } else {
      reset({
        name: "",
        contactPerson: "",
        email: "",
        phone: "",
        city: "",
        address: "",
        licenseNo: "",
        commissionRate: "",
        status: "Active",
      });
    }
  }, [mode, editRow, reset]);

  // Show success message
  useEffect(() => {
    if (isPostSuccess || isPutSuccess) {
      setShowSuccess(true);
      const timer = setTimeout(() => {
        setShowSuccess(false);
        onSuccess?.();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isPostSuccess, isPutSuccess, onSuccess]);

  const onSubmit = async (values) => {
    const payload = {
      ...values,
      agencyId: currentAgency.agencyid,
      commissionRate: values.commissionRate
        ? parseFloat(values.commissionRate)
        : null,
      status: Number(values.status),
    };

    console.log("Final Payload Sent:", payload); // ✅ check here

    if (mode === "add") {
      await postVendor(payload).unwrap();
    } else if (mode === "edit" && editRow?.id) {
      await putVendor({ id: editRow.id, ...payload }).unwrap();
    }
  };

  const SectionHeader = ({ icon, title, subtitle }) => (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Box
          sx={{
            p: 1,
            borderRadius: 2,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}20, ${theme.palette.primary.light}20)`,
            color: theme.palette.primary.main,
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="h6" fontWeight="600" color="primary.main">
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );

  return (
    <FormProvider {...methods}>
      <Fade in timeout={500}>
        <Box sx={{ maxWidth: 1200, mx: "auto" }}>
          {/* Header Card */}
          <Card
            sx={{
              boxShadow: "none", // ✅ removes shadow
              border: "none", // ✅ removes border
              mb: 0, // ✅ no bottom margin
              pb: 0, // ✅ no bottom padding
              background: "transparent", // ✅ makes it visually flat
            }}
          >
            <Box sx={{ textAlign: "center", pb: 0, mb: 0 }}>
              <Typography variant="h4" fontWeight="700">
                {mode === "add" ? "Add New Vendor" : "Edit Vendor"}
              </Typography>
              <Typography variant="h6" color="text.secondary">
                {mode === "add"
                  ? "Register a new real estate vendor partner"
                  : `Editing: ${watchedName || editRow?.name || "Vendor"}`}
              </Typography>
            </Box>
          </Card>

          {/* Form Card */}
          <Card
            sx={{
              p: { xs: 3, md: 4 },
              borderRadius: 4,
              boxShadow: "0 8px 40px rgba(0,0,0,0.12)",
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={2}>
                {/* Basic Information Section */}
                <Grid item xs={12}>
                  <SectionHeader
                    icon={<Business fontSize="large" />}
                    title="Basic Information"
                    subtitle="Vendor company details and primary contact"
                  />
                </Grid>

                {/* Name */}
                <Grid item xs={12} md={6}>
                  <Controller
                    name="name"
                    control={control}
                    rules={{ required: "Vendor name is required" }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Vendor / Company Name"
                        fullWidth
                        size="medium"
                        error={!!errors.name}
                        helperText={errors.name?.message}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 3,
                            transition: "all 0.2s ease-in-out",
                            "&:hover": {
                              boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.1)",
                            },
                            "&.Mui-focused": {
                              boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.2)",
                            },
                          },
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Contact Person */}
                <Grid item xs={12} md={6}>
                  <Controller
                    name="contactPerson"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Contact Person"
                        fullWidth
                        size="medium"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 3,
                          },
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Contact Details Section */}
                <Grid item xs={12}>
                  <SectionHeader
                    icon={<ContactPhone fontSize="large" />}
                    title="Contact Details"
                    subtitle="Primary communication channels"
                  />
                </Grid>

                {/* Email */}
                <Grid item xs={12} md={6}>
                  <Controller
                    name="email"
                    control={control}
                    rules={{
                      pattern: {
                        value: /^\S+@\S+$/i,
                        message: "Please enter a valid email address",
                      },
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Email Address"
                        type="email"
                        fullWidth
                        size="medium"
                        error={!!errors.email}
                        helperText={errors.email?.message}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 3,
                          },
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Phone */}
                <Grid item xs={12} md={6}>
                  <Controller
                    name="phone"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Phone Number"
                        fullWidth
                        size="medium"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 3,
                          },
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Location Section */}
                <Grid item xs={12}>
                  <SectionHeader
                    icon={<LocationOn fontSize="large" />}
                    title="Location Information"
                    subtitle="Vendor's operational location"
                  />
                </Grid>

                {/* City */}
                <Grid item xs={12} md={6}>
                  <Controller
                    name="city"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="City"
                        fullWidth
                        size="medium"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 3,
                          },
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* License Number */}
                <Grid item xs={12} md={6}>
                  <Controller
                    name="licenseNo"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="License / Registration No"
                        fullWidth
                        size="medium"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 3,
                          },
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Address */}
                <Grid item xs={12}>
                  <Controller
                    name="address"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Full Address"
                        fullWidth
                        size="medium"
                        multiline
                        rows={3}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 3,
                          },
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Business Details Section */}
                <Grid item xs={12}>
                  <SectionHeader
                    icon={<Assignment fontSize="large" />}
                    title="Business Details"
                    subtitle="Commercial terms and status"
                  />
                </Grid>

                {/* Commission Rate */}
                <Grid item xs={12} md={6}>
                  <Controller
                    name="commissionRate"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Commission Rate"
                        type="number"
                        fullWidth
                        size="medium"
                        InputProps={{
                          endAdornment: (
                            <Typography color="text.secondary">%</Typography>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 3,
                          },
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Status */}
                <Grid item xs={12} md={6}>
                  <Controller
                    name="status"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Vendor Status"
                        fullWidth
                        select
                        size="medium"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 3,
                          },
                        }}
                      >
                        <MenuItem value={1}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                            }}
                          >
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                bgcolor: "success.main",
                                boxShadow: "0 0 8px rgba(76, 175, 80, 0.5)",
                              }}
                            />
                            <Typography fontWeight="500">Active</Typography>
                          </Box>
                        </MenuItem>

                        <MenuItem value={0}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                            }}
                          >
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                bgcolor: "error.main",
                                boxShadow: "0 0 8px rgba(244, 67, 54, 0.5)",
                              }}
                            />
                            <Typography fontWeight="500">Inactive</Typography>
                          </Box>
                        </MenuItem>
                      </TextField>
                    )}
                  />
                </Grid>

                {/* Submit Button */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      mt: 4,
                      pt: 4,
                      borderTop: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={isPosting || isUpdating || !isDirty || !isValid}
                      startIcon={mode === "add" ? <Save /> : <Update />}
                      sx={{
                        px: 8,
                        py: 2,
                        fontWeight: "bold",
                        borderRadius: 3,
                        textTransform: "none",
                        fontSize: "1.1rem",
                        minWidth: 200,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        boxShadow: "0 8px 25px rgba(0,0,0,0.2)",
                        "&:hover": {
                          boxShadow: "0 12px 35px rgba(0,0,0,0.3)",
                          transform: "translateY(-2px)",
                        },
                        "&:disabled": {
                          background: theme.palette.action.disabled,
                          transform: "none",
                          boxShadow: "none",
                        },
                        transition: "all 0.3s ease-in-out",
                      }}
                    >
                      {isPosting || isUpdating
                        ? "Processing..."
                        : mode === "add"
                        ? "Create Vendor"
                        : "Update Vendor"}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </Card>
        </Box>
      </Fade>
    </FormProvider>
  );
};

export default VendorFormPage;
